// SearchBar.js
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import './SearchBar.css';
import { useLanguage } from '../i18n/LanguageContext';

function SearchBar({ destinations }) {
    const navigate = useNavigate();
    const { t } = useLanguage();
    const [searchTerm, setSearchTerm] = useState('');
    const [showOverlay, setShowOverlay] = useState(false);
    const searchBoxRef = useRef(null);

    // normalize for case + diacritics
    const normalize = (s = '') =>
        s
            .toString()
            .normalize('NFD')
            .replace(/\p{Diacritic}/gu, '')
            .toLowerCase()
            .trim();

    // Close overlay when clicking outside or pressing Esc
    useEffect(() => {
        function handleClickOutside(e) {
            if (searchBoxRef.current && !searchBoxRef.current.contains(e.target)) {
                setShowOverlay(false);
            }
        }
        function handleEsc(e) {
            if (e.key === 'Escape') {
                setShowOverlay(false);
            }
        }
        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('keydown', handleEsc);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('keydown', handleEsc);
        };
    }, []);

    const handleChange = (e) => {
        const value = e.target.value;
        setSearchTerm(value);
        setShowOverlay(value.trim().length > 0);
    };

    const filteredResults = useMemo(() => {
        const q = normalize(searchTerm);
        if (!q) return [];

        return (destinations || []).filter((item) => {
            const hay = normalize(item.name) + ' ' + normalize(item.subtitle || '');
            return hay.includes(q);
        });
    }, [searchTerm, destinations]);

    const handleSelect = (destination) => {
        setSearchTerm(destination.name);
        setShowOverlay(false);
        navigate(`/region/${destination.regionCode}${window.location.search}`);
    };

    const handleClear = () => {
        setSearchTerm('');
        setShowOverlay(false);
    };

    return (
        <div className="search-bar-container" ref={searchBoxRef}>
            {showOverlay && (
                <div className="search-overlay" onMouseDown={() => setShowOverlay(false)} />
            )}

            <div className="search-box">
                <span className="search-icon-left">
                    <img src={`${process.env.PUBLIC_URL}/icons/airport-plane.svg`} width={14} height={14} alt="" aria-hidden />
                </span>
                <input
                    type="text"
                    className="search-input"
                    placeholder={t('search.placeholder')}
                    value={searchTerm}
                    onChange={handleChange}
                />
                {searchTerm && (
                    <button className="search-clear-button" onClick={handleClear} aria-label={t('search.clear')}>
                        ✕
                    </button>
                )}
            </div>


            {showOverlay && filteredResults.length > 0 && (
                <div className="search-results">
                    {filteredResults.map((item) => (
                        <div
                            className="search-result-item"
                            key={`${item.regionCode}-${item.name}`} // ensure uniqueness
                            onClick={() => handleSelect(item)}
                        >
                            {item.shopImageUrl && (
                                <img
                                    src={item.shopImageUrl}
                                    alt={item.name}
                                    className="result-item-image"
                                />
                            )}

                            <div className="result-item-main">
                                <span className="result-item-name">{item.name}</span>
                                {item.subtitle && (
                                    <span className="result-item-subtitle">{item.subtitle}</span>
                                )}
                            </div>

                            {item.startingPrice && (
                                <span className="result-item-tag">Rp {item.startingPrice}</span>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

export default SearchBar;
